import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core'
import { Call, CallListType, CallsService } from '../calls.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatDialog } from '@angular/material/dialog'
import { PreviousMeetingCardComponent } from '../previous-meeting-card/previous-meeting-card.component'
import { UpcomingMeetingCardComponent } from '../upcoming-meeting-card/upcoming-meeting-card.component'
import { OngoingMeetingCardComponent } from '../ongoing-meeting-card/ongoing-meeting-card.component'
import { AsyncPipe, DatePipe } from '@angular/common'
import { addDays, isBefore, isSameDay, startOfDay, subDays } from 'date-fns'
import { ActivatedRoute, Router } from '@angular/router'
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map } from 'rxjs'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { FormsModule } from '@angular/forms'
import { MatTooltip } from '@angular/material/tooltip'
import { WaIntersectionObservee, WaIntersectionObserverDirective } from '@ng-web-apis/intersection-observer'

@Component({
  selector: 'app-call-list',
  standalone: true,
  imports: [
    AsyncPipe,
    SpinnerComponent,
    PreviousMeetingCardComponent,
    UpcomingMeetingCardComponent,
    OngoingMeetingCardComponent,
    TranslateModule,
    MatSlideToggle,
    FormsModule,
    MatTooltip,
    WaIntersectionObservee,
    WaIntersectionObserverDirective,
  ],
  providers: [DatePipe],
  templateUrl: './call-list.component.html',
  styleUrl: './call-list.component.scss',
})
export class CallListComponent implements OnChanges {
  @Input()
  calls!: Call[]

  @Input()
  hasMore!: boolean

  @Input()
  isFetchingNextPage!: boolean

  @Input()
  callListType!: CallListType

  @Input()
  useGroupingByDate = false

  @Input()
  activeMatch: string | null = null

  @Input()
  activeCall: Call | null = null

  @Output()
  fetchNextPage = new EventEmitter<void>()

  @Output()
  hoverCall = new EventEmitter<Call>()

  @Output()
  callGotIntoView = new EventEmitter<number>()

  @ViewChild('callsContainer')
  callsContainer!: ElementRef

  router = inject(Router)
  route = inject(ActivatedRoute)
  callsService = inject(CallsService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  datePipe = inject(DatePipe)

  showOnlyToBeJoinedCalls$ = this.route.queryParams.pipe(
    map((params) => params['showOnlyToBeJoined'] === 'true'),
    distinctUntilChanged()
  )
  groupedCalls$ = new BehaviorSubject<{ date: string; calls: Call[] }[]>([])
  ongoingCalls$ = this.callsService.getOngoingCalls().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data || [])
  )

  filteredGroupedCalls$ = combineLatest([this.showOnlyToBeJoinedCalls$, this.groupedCalls$]).pipe(
    map(([showOnlyToBeJoinedCalls, groupedCalls]) => {
      if (!showOnlyToBeJoinedCalls) {
        return groupedCalls
      }
      return groupedCalls
        .map((group) => ({
          ...group,
          calls: group.calls.filter((call) => call.isSetToJoin || call.botInstructedToJoinByOtherUsers),
        }))
        .filter((group) => group.calls.length > 0)
    })
  )

  onCallVisible(entries: IntersectionObserverEntry[], call: Call): void {
    const containerRect = this.callsContainer.nativeElement.getBoundingClientRect()
    entries.forEach((entry) => {
      const isAtTopEdge = entry.boundingClientRect.top <= containerRect.top
      if (isAtTopEdge && entry.intersectionRatio > 0) {
        const currentCallId = this.route.snapshot.queryParamMap.get('callId')
        if (currentCallId === null || call.id !== parseInt(currentCallId)) {
          this.callGotIntoView.emit(call.id)
        }
      }
    })
  }

  scrollToCall(callId: number) {
    const callElement = this.callsContainer.nativeElement.querySelector(`[data-call-id="${callId}"]`)
    if (callElement) {
      callElement.scrollIntoView({ block: 'start' })
    } else {
      console.warn(`Call element not found for id ${callId}. Skipping scrolling.`)
    }
  }

  loadMore(): void {
    if (this.isFetchingNextPage) {
      return
    }
    this.fetchNextPage.emit()
  }

  toggleShowOnlyToBeJoinedCalls(value: boolean) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { showOnlyToBeJoined: value },
      queryParamsHandling: 'merge',
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['calls']) {
      this.groupedCalls$.next(this.groupAndSortCallsByDay(this.calls))
    }
  }

  formatUpcomingDate(date: Date): string {
    const now = new Date()
    if (isSameDay(date, now)) {
      return this.translateService.instant('COMMON.LATER_TODAY')
    }
    if (isSameDay(date, addDays(now, 1))) {
      return this.translateService.instant('COMMON.TOMORROW')
    }
    // If the date is somehow in the past (e.g. due to timezone issues), show "Today" as a fallback
    if (isBefore(date, startOfDay(now))) {
      return this.translateService.instant('COMMON.LATER_TODAY')
    }
    return this.datePipe.transform(date, 'dd MMMM', 'UTC') as string
  }

  formatPreviousDate(date: Date): string {
    const now = new Date()
    if (isSameDay(date, now)) {
      return this.translateService.instant('COMMON.TODAY')
    }
    if (isSameDay(date, addDays(now, 1))) {
      return this.translateService.instant('COMMON.TOMORROW')
    }
    if (isSameDay(date, subDays(now, 1))) {
      return this.translateService.instant('COMMON.YESTERDAY')
    }
    return this.datePipe.transform(date, 'EEEE, dd MMMM', 'UTC') as string
  }

  groupAndSortCallsByDay(calls: Call[]): { date: string; calls: Call[] }[] {
    const groupedCalls = Object.groupBy(calls, (call) => {
      const date = new Date(call.date)
      return date.toISOString().split('T')[0]
    }) as Required<{ [date: string]: Call[] }>
    return Object.entries(groupedCalls)
      .toSorted(([dateA], [dateB]) =>
        // For upcoming calls we want the nearest future date first, for previous calls we want the nearest past date first
        this.callListType === 'upcoming' ? new Date(dateA).getTime() - new Date(dateB).getTime() : new Date(dateB).getTime() - new Date(dateA).getTime()
      )
      .map(([date, calls]) => ({
        date: this.callListType === 'upcoming' ? this.formatUpcomingDate(new Date(date)) : this.formatPreviousDate(new Date(date)),
        calls,
      }))
  }
}
